import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch, useSelector } from 'react-redux';
import { useIdle } from 'react-use';

import { createStyles, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { filter, isEmpty, last } from 'lodash';
import { ILumaBotFlowTemplate } from 'ui-model';

import { initApp, signOut } from '@lib/actions';
import { usePrevious } from '@lib/hooks';
import { usersResource } from '@lib/resources';
import { selectCurrentUser } from '@lib/selectors';

import { Typography } from 'components/v2';

import { AppBar, Avatar, ContentLoader, Input, Messages } from './components';
import { LumaBotContextProps, LumaBotProvider } from './LumaBot.context';
import useLumaBotController from './useLumaBotController';
import IconPoweredBy from './WIP/ui-core/IconPoweredBy';
import { ELumaBotFlowType, EMessageType } from './WIP/ui-model/types';

const styles = () =>
  createStyles({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    messages: {
      flex: 1,
    },
    appBar: {
      minHeight: 56,
    },
    poweredByLuma: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },
    iconPoweredBy: {
      fontSize: '14px',
    },
  });

const intlMessages = defineMessages({
  poweredByLuma: {
    id: 'modules.LumaBot.poweredByLuma',
    defaultMessage: '{powered} Powered by Luma Health',
  },
});

const oneMinute = 60e3;

export interface LumaBotProps extends WithStyles<typeof styles>, React.HTMLAttributes<HTMLDivElement> {
  flowTemplates?: ILumaBotFlowTemplate[];
  flowId?: string;
  variant?: LumaBotContextProps['variant'];
  minutesKnockKnock?: number;
  minutesSessionTimeout?: number;
  readOnly?: boolean;
  isPopper?: boolean;
  onClose: () => void;
  onMessagesUnread: (count?: number) => void;
  onMessagesRead: () => void;
}

const LumaBot: React.FC<LumaBotProps> = ({
  classes,
  className,
  flowTemplates,
  flowId,
  variant,
  minutesKnockKnock = 10,
  minutesSessionTimeout = 15,
  readOnly,
  isPopper,
  onClose,
  onMessagesUnread,
  onMessagesRead,
  ...rest
}) => {
  const lumaBotContext = useLumaBotController(flowTemplates, flowId, variant, isPopper);
  const { settings, loading, messages, pushFlowTemplate, pushAnswer, revertMessage, clear } = lumaBotContext;
  const focused = useRef(false);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const widgetUser = useSelector(usersResource.makeSelectEntityFiltered({ $isWidgetUser: true }));
  const currentUser = useSelector(selectCurrentUser)!;

  const isIdle10 = useIdle(minutesKnockKnock * oneMinute);
  const isIdle15 = useIdle(minutesSessionTimeout * oneMinute);

  // knock-knock
  useEffect(() => {
    if (isIdle10 && !readOnly) pushFlowTemplate(ELumaBotFlowType.KnockKnock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle10, readOnly]);

  // session-timeout
  useEffect(() => {
    if (isIdle15 && !readOnly && !currentUser.$isWidgetUser) {
      dispatch(signOut({}));
      dispatch(
        initApp.request({
          location: { query: { user: widgetUser ? widgetUser.id : 'forceError' } } as any,
          meta: {
            callbackSuccess: () => {
              clear();
              pushFlowTemplate(ELumaBotFlowType.SessionTimeout);
            },
            // NOTE: This should never happen... reloading just in case
            callbackError: () => window.location.reload(),
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle15, readOnly]);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        focused.current = false;
      }
    });
  }, []);

  // play notification for new incoming message
  const prevMessages = usePrevious(messages);
  useEffect(() => {
    if (!focused.current) {
      const countMessages = filter(messages, { type: EMessageType.Inbound }).length;
      const countPrevMessages = filter(prevMessages, { type: EMessageType.Inbound }).length;
      if (countPrevMessages && countPrevMessages < countMessages) {
        onMessagesUnread(countMessages - countPrevMessages);
      }
    }
  }, [messages, prevMessages, onMessagesUnread]);

  if (isEmpty(messages)) {
    return <ContentLoader />;
  }

  return (
    <LumaBotProvider {...lumaBotContext}>
      <AppBar onClose={onClose} position="relative" />
      <div
        className={classnames(classes.root, className)}
        onClick={() => {
          focused.current = true;
          onMessagesRead();
        }}
        onFocus={() => {
          focused.current = true;
          onMessagesRead();
        }}
        onBlur={() => {
          focused.current = false;
        }}
        {...rest}
      >
        <Messages
          className={classes.messages}
          messages={messages}
          avatarInbound={<Avatar size="xxsmall" src={settings.avatar as string} />}
          onSubmit={pushAnswer}
          onRevert={revertMessage}
        />
        <Input message={last(messages)} disabled={loading} multiline rowsMax={3} onSubmit={pushAnswer} />
        {settings.includePoweredByLuma && (
          <Typography className={classes.poweredByLuma} variant="bW600F12" color="textSecondary">
            {formatMessage(intlMessages.poweredByLuma, {
              powered: <IconPoweredBy className={classes.iconPoweredBy} />,
            })}
          </Typography>
        )}
      </div>
    </LumaBotProvider>
  );
};

export default withStyles(styles, { name: 'LumaBot' })(LumaBot);
